/**
 * Permission values matching backend implementation
 */
export enum PermissionValue {
  BoxView = 1,
  BoxManage = 2,
  BoxAdmin = 3,
  LightView = 10,
  LightManage = 11,
  SparePartsView = 20,
  SparePartsManage = 21,
  UserManage = 30,
  PalletsView = 40,
  PalletsManage = 41
}

/**
 * Permission name constants matching backend JWT claims
 */
export const PERMISSIONS = {
  BoxView: 'BoxView',
  BoxManage: 'BoxManage',
  BoxAdmin: 'BoxAdmin',
  LightView: 'LightView',
  LightManage: 'LightManage',
  SparePartsView: 'SparePartsView',
  SparePartsManage: 'SparePartsManage',
  UserManage: 'UserManage',
  PalletsView: 'PalletsView',
  PalletsManage: 'PalletsManage'
} as const;

/**
 * Permission type for type safety
 */
export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Permission interface for API responses
 */
export interface PermissionInfo {
  permission?: number; // API response field
  id?: number; // Fallback/computed field
  name: string;
  value: PermissionValue;
  description?: string; // API response field
}

/**
 * Helper function to get permission name from value
 */
export function getPermissionName(value: PermissionValue): string {
  const mapping: Record<PermissionValue, string> = {
    [PermissionValue.BoxView]: PERMISSIONS.BoxView,
    [PermissionValue.BoxManage]: PERMISSIONS.BoxManage,
    [PermissionValue.BoxAdmin]: PERMISSIONS.BoxAdmin,
    [PermissionValue.LightView]: PERMISSIONS.LightView,
    [PermissionValue.LightManage]: PERMISSIONS.LightManage,
    [PermissionValue.SparePartsView]: PERMISSIONS.SparePartsView,
    [PermissionValue.SparePartsManage]: PERMISSIONS.SparePartsManage,
    [PermissionValue.UserManage]: PERMISSIONS.UserManage,
    [PermissionValue.PalletsView]: PERMISSIONS.PalletsView,
    [PermissionValue.PalletsManage]: PERMISSIONS.PalletsManage
  };
  return mapping[value] || '';
}

/**
 * Helper function to get permission value from name
 */
export function getPermissionValue(name: string): PermissionValue | null {
  const mapping: Record<string, PermissionValue> = {
    [PERMISSIONS.BoxView]: PermissionValue.BoxView,
    [PERMISSIONS.BoxManage]: PermissionValue.BoxManage,
    [PERMISSIONS.BoxAdmin]: PermissionValue.BoxAdmin,
    [PERMISSIONS.LightView]: PermissionValue.LightView,
    [PERMISSIONS.LightManage]: PermissionValue.LightManage,
    [PERMISSIONS.SparePartsView]: PermissionValue.SparePartsView,
    [PERMISSIONS.SparePartsManage]: PermissionValue.SparePartsManage,
    [PERMISSIONS.UserManage]: PermissionValue.UserManage,
    [PERMISSIONS.PalletsView]: PermissionValue.PalletsView,
    [PERMISSIONS.PalletsManage]: PermissionValue.PalletsManage
  };
  return mapping[name] || null;
}
